import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import {
  AssociatedAlertsModal,
} from '@avtechsoftware/react-component-library/src/components/metrics/AssociatedAlertsModal/AssociatedAlertsModal';
import AvtechApplicationCodes from '@avtechsoftware/ra-javascript-utilities/src/types/AvtechApplicationCodes';
import { useHistory } from 'react-router-dom';
import { OperatorTypes } from '@avtechsoftware/ra-javascript-utilities/src/types/OperatorTypes';
import PaginatedChannelListNavigation from '../../../paginatedchannellist/PaginatedChannelListNavigation';
import {
  fetchFilteredChannelList,
  selectActiveChannelSearchParams,
  selectChannelList,
  selectChannelListColumnVisibility,
  selectChannelListCrudStatus, selectChannelListRowsPerPage,
  setAppliedChannelSearch,
  setChannelListPage,
} from '../../../../redux/slices/channelListSlice/channelListSlice';
import ChannelListTable from '../../../paginatedchannellist/ChannelListTable';
import { selectSessionInformation } from '../../../../redux/slices/sessionSlice';
import { mapApiChannelToTableRow } from '../../../paginatedchannellist/channelTableViewModel';
import {
  addChannelIdsToSelectedList,
  removeChannelIdsFromSelectedList,
  selectIsEditingAlertForm,
  selectSensorsData,
  setIsFormValid,
  setSelectedChannelType,
  setSelectedReadingType,
} from '../../../../redux/slices/alertFormSlice/alertFormSlice';
import RaaAuthenticatedRemoteService from '../../../../utility/RaaAuthenticatedRemoteService';
import envConfig from '../../../../envConfig';
import { selectActiveAlertEventList } from '../../../../redux/slices/alertEvents/alertEventsSlice';
import facetType from '../../../../utility/types/facetType';
import { parseChannelKey } from '../../../../utility/channelUtils';
import SearchType from '../../../../utility/types/SearchType';
import ChannelTypeFacet from '../../../../classes/facets/ChannelTypeFacet/ChannelTypeFacet';
import { selectRoomAlertDeviceList, selectSensorMap } from '../../../../redux/slices/deviceList/deviceListSlice';
import useFilters from '../../../hooks/useFilters/useFilters';
import ChannelSearchFilters from './ChannelSearchFilters';
import CannotAddMultipleAlertsModal from './CannotAddMultipleAlertsModal';
import DeviceSelector from './DeviceSelector';
import ChannelTypeSelector from './ChannelTypeSelector';
import { CrudStatus } from '../../../../utility/types/CrudStatus';
import { buildChannelTypeFiltersFromChannelKey } from '../AlertFormUtility';
import ChannelListTableSkeleton from './ChannelListTableSkeleton';

/* istanbul ignore next */
function SensorSelectorTable() {
  const dispatch = useDispatch();
  const history = useHistory();
  const deviceList = useSelector(selectRoomAlertDeviceList);
  const sensorMap = useSelector(selectSensorMap);
  const rawChannelList = useSelector(selectChannelList);
  const channelTypeFacet = useMemo(() => new ChannelTypeFacet(), []);
  const channelTypeSelectOptions = channelTypeFacet.getValueSelectOptions();
  const activeSearchParams = useSelector(selectActiveChannelSearchParams);
  const channelListColumnVisibility = useSelector(selectChannelListColumnVisibility);
  const channelListCrudStatus = useSelector(selectChannelListCrudStatus);
  const channelListRowsPerPage = useSelector(selectChannelListRowsPerPage);
  const isEdit = useSelector(selectIsEditingAlertForm);
  const sessionInformation = useSelector(selectSessionInformation);
  const activeSubscription = sessionInformation && sessionInformation.activeSubscription;
  const hasBulkCreation = activeSubscription && activeSubscription.subscriptionTierLevel > 4;
  const activeAlertEventList = useSelector(selectActiveAlertEventList);
  const getDevice = (deviceMacAddress) => deviceList.find((d) => d.uuid === deviceMacAddress);
  const { selectedChannels } = useSelector(selectSensorsData);
  const [alertList, setAlertList] = useState([]);
  const [modalAlertIds, setModalAlertIds] = useState([]);
  const [showAccountUpgradeModal, setShowAccountUpgradeModal] = useState(false);
  const prevSelectedLength = useRef(selectedChannels.length);

  const {
    filters,
    setFilters,
    updateFilters,
    removeFilter,
    getFilterTooltip,
    getFilterByFacetTypeId,
  } = useFilters({
    initialFilters: activeSearchParams.filters || [],
    searchType: SearchType.CHANNEL,
  });

  const selectedDeviceFilters = getFilterByFacetTypeId(facetType.DEVICE_ID);
  const selectedDevices = deviceList
    .filter((device) => selectedDeviceFilters.some((f) => f.value1 === device.id))
    .map((device) => ({
      label: device.displayName,
      value: device.id,
    }));
  const selectedChannelFilters = getFilterByFacetTypeId(facetType.CHANNEL_TYPE);
  const selectedChannelOptions = channelTypeSelectOptions
    .filter((opt) => opt.value.some((val) => selectedChannelFilters.some((f) => f.value1 === val)));

  const handleDeviceChange = (devices) => {
    // eslint-disable-next-line no-nested-ternary
    const normalized = Array.isArray(devices) ? devices : (devices ? [devices] : []);
    const deviceUuids = normalized.map((opt) => opt.value);
    const newFilters = deviceUuids.map((uuid) => ({
      id: SearchType.CHANNEL,
      facetId: facetType.DEVICE_ID,
      operatorId: OperatorTypes.EQUAL_TO_VALUE,
      value1: uuid,
      value2: '',
    }));
    updateFilters(newFilters, facetType.DEVICE_ID);
  };

  const handleChannelTypeChange = (options) => {
    if (selectedChannels.length > 0) return;
    // eslint-disable-next-line no-nested-ternary
    const normalized = Array.isArray(options) ? options : (options ? [options] : []);
    const flattenedValues = normalized.flatMap((opt) => opt.value || []);
    const newFilters = flattenedValues.map((val) => ({
      id: SearchType.CHANNEL,
      facetId: facetType.CHANNEL_TYPE,
      operatorId: OperatorTypes.EQUAL_TO_VALUE,
      value1: val,
      value2: '',
    }));
    updateFilters(newFilters, facetType.CHANNEL_TYPE);
  };

  const displayOptions = {
    scale: sessionInformation.temperatureScale,
    timeZone: sessionInformation.timeZone,
    airSpeedScale: sessionInformation.airSpeedScale,
  };

  const getTargetSensor = (channelKey) => {
    const {
      deviceMacAddress, sensorMacAddress, channelGroupType, channelGroupIndex,
    } = parseChannelKey(channelKey);
    const channelGroupList = sensorMap[deviceMacAddress] || [];
    const channelGroupIdentifier = `${sensorMacAddress}_${channelGroupType}_${channelGroupIndex}`;
    return channelGroupList
      .find((sensor) => `${sensor.channelGroupMacAddress}_${sensor.channelGroupTypeId}_${sensor.channelGroupIndex}` === channelGroupIdentifier);
  };

  const formattedChannelList = useMemo(
    () => rawChannelList
      .map((c) => mapApiChannelToTableRow(c, displayOptions, getDevice, getTargetSensor)),
    [rawChannelList, displayOptions.scale, displayOptions.timeZone],
  );

  const availableChannelList = useMemo(
    () => formattedChannelList.filter(
      (row) => !selectedChannels.includes(row.channelKey),
    ),
    [formattedChannelList, selectedChannels],
  );

  const onAddAllChannels = () => {
    const keysToAdd = availableChannelList.map((row) => row.channelKey);

    if (keysToAdd.length === 0 || isEdit) return;

    dispatch(
      addChannelIdsToSelectedList({
        data: { newChannelIds: keysToAdd },
      }),
    );
  };

  // eslint-disable-next-line consistent-return
  const getSearchedChannels = async (filtersToApply) => {
    try {
      dispatch(setChannelListPage(0));
      await dispatch(fetchFilteredChannelList({ filters: filtersToApply }));
      dispatch(setAppliedChannelSearch({ filters: filtersToApply }));

      return filtersToApply;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to retrieve filtered channel list:', error);
    }
  };

  const safeRemoveFilter = (facetId) => {
    // Prevent removing Channel Type filter while channels are selected
    if (facetId === facetType.CHANNEL_TYPE && selectedChannels.length > 0) {
      return;
    }
    removeFilter(facetId);
  };

  const onAddChannel = async (newChannel) => {
    const { channelKey, readingType, channelType } = newChannel;
    if (isEdit && selectedChannels.length >= 1) {
      // cant add multiple sensors if editing
      dispatch(
        removeChannelIdsFromSelectedList({
          data: { channelIdsToRemove: selectedChannels },
        }),
      );
    }
    try {
      const channel = availableChannelList.find(
        (row) => row.channelKey === channelKey,
      );
      if (!channel) return;
      // If selecting first channel, add a channelType filter automatically
      if (selectedChannels.length === 0) {
        const newFilter = buildChannelTypeFiltersFromChannelKey(
          channelKey,
          OperatorTypes.EQUAL_TO_VALUE,
        );
        const existingFilters = filters || [];
        const cleaned = existingFilters.filter(
          (f) => !(
            f.facetId === newFilter[0].facetId
            && f.value1 === newFilter[0].value1
          ),
        );
        const filtersToApply = [...cleaned, ...newFilter];
        setFilters(filtersToApply);
        await getSearchedChannels(filtersToApply);
      }
      if (selectedChannels.length >= 1 && !hasBulkCreation) {
        setShowAccountUpgradeModal(true);
      }
      if (!selectedChannels.includes(channelKey)) {
        dispatch(
          addChannelIdsToSelectedList({
            data: { newChannelIds: [channelKey] },
          }),
        );
        dispatch(setSelectedChannelType({ channelType }));
        dispatch(setSelectedReadingType({ readingType }));
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error adding channel:', error);
    }
  };

  const findAlertById = (id) => alertList.find((alert) => alert.id === id);

  useEffect(() => {
    RaaAuthenticatedRemoteService.globalInstance()
      .get(`${envConfig.apiUrl}/api/v1/alerts`)
      .then((response) => {
        setAlertList([...response.alertList]);
      });
  }, []);

  useEffect(() => {
    const initial = activeSearchParams.filters || [];
    setFilters(initial);
    getSearchedChannels(initial);
  }, []);

  // automatically refetch whenever filters change
  useEffect(() => {
    if (!filters) return;
    getSearchedChannels(filters);
  }, [filters]);

  useEffect(() => {
    // only remove channelType if we just went from >0 → 0
    if (prevSelectedLength.current > 0 && selectedChannels.length === 0) {
      const hasChannelType = filters.some(
        (f) => f.facetId === facetType.CHANNEL_TYPE,
      );
      if (hasChannelType) {
        removeFilter(facetType.CHANNEL_TYPE);
      }
    }
    prevSelectedLength.current = selectedChannels.length;
  }, [selectedChannels, filters]);

  useEffect(() => {
    if (!hasBulkCreation) {
      if (selectedChannels && selectedChannels.length > 1) {
        dispatch(setIsFormValid(false));
      } else {
        dispatch(setIsFormValid(selectedChannels.length === 1));
      }
    }
  }, [dispatch, selectedChannels.length, hasBulkCreation]);

  const isLoadingChannelList = channelListCrudStatus === CrudStatus.PENDING;

  return (
    <>
      <div className="row">
        <div className="col-3">
          <DeviceSelector
            onUpdateSelectedDevices={handleDeviceChange}
            selectedDevices={selectedDevices}
            deviceList={deviceList}
          />
        </div>

        <div className="col-3">
          <ChannelTypeSelector
            onUpdateSelectedChannelType={handleChannelTypeChange}
            channelTypeList={channelTypeSelectOptions}
            // channelTypeList={rawChannelList.map((c) => c.channelType)}
            selectedChannelOptions={selectedChannelOptions}
          />
        </div>

        <div className="col-6">
          <ChannelSearchFilters
            filters={filters}
            updateFilters={updateFilters}
            removeFilter={safeRemoveFilter}
            getFilterTooltip={getFilterTooltip}
            getFilterByFacetTypeId={getFilterByFacetTypeId}
            onApply={getSearchedChannels}
          />
        </div>
      </div>

      <div className="clearfix pr-2 pt-2 pb-0">
        {
          !isEdit && (
            <div className="d-flex float-left">
              <Button
                variant="secondary"
                className="btn-sm"
                onClick={onAddAllChannels}
                disabled={selectedChannels.length === 0}
                title="Adds all sensors currently visible on the table"
              >
                <FontAwesomeIcon icon={faPlus} size="sm" className="mr-1" />
                <span>Add All</span>
              </Button>
            </div>
          )
        }
        <div className="float-right">
          <PaginatedChannelListNavigation />
        </div>
      </div>

      {isLoadingChannelList ? (
        <ChannelListTableSkeleton
          rows={channelListRowsPerPage}
          channelListColumnVisibility={channelListColumnVisibility}
        />
      ) : (
        <ChannelListTable
          onAddChannel={onAddChannel}
          onShowAlerts={(alertIds) => setModalAlertIds(alertIds)}
          channelList={availableChannelList}
          channelListColumnVisibility={channelListColumnVisibility}
        />
      )}

      {modalAlertIds.length > 0 && (
        <AssociatedAlertsModal
          activeEventAlertList={activeAlertEventList}
          getAlertById={findAlertById}
          alertIdList={modalAlertIds}
          onClose={() => setModalAlertIds([])}
          redirectToAlertDetails={(alertId) => history.push(`/s/alerts/${alertId}`)}
          applicationCode={AvtechApplicationCodes.RAA}
        />
      )}
      {
        showAccountUpgradeModal && (
          <CannotAddMultipleAlertsModal
            show={showAccountUpgradeModal}
            onHide={() => setShowAccountUpgradeModal(false)}
          />
        )
      }
    </>
  );
}

export default SensorSelectorTable;
